import { getScaledShapePoints } from './frameShapes';

export const MATERIALS = [
  { label: '1.50 sph', index: 1.50, design: 'sph', coeffMinus: 1.00, coeffPlus: 1.00 },
  { label: '1.50 asph', index: 1.50, design: 'asph', coeffMinus: 0.96, coeffPlus: 0.90 },

  { label: '1.60 sph', index: 1.60, design: 'sph', coeffMinus: 1.00, coeffPlus: 1.00 },
  { label: '1.60 asph', index: 1.60, design: 'asph', coeffMinus: 0.94, coeffPlus: 0.90 },

  { label: '1.67 sph', index: 1.67, design: 'sph', coeffMinus: 1.00, coeffPlus: 1.00 },
  { label: '1.67 asph', index: 1.67, design: 'asph', coeffMinus: 0.95, coeffPlus: 0.92 },

  { label: '1.74 sph', index: 1.74, design: 'sph', coeffMinus: 1.00, coeffPlus: 1.00 },
  { label: '1.74 asph', index: 1.74, design: 'asph', coeffMinus: 0.97, coeffPlus: 0.95 },
];

export const DEFAULT_INDICES = [1.5, 1.6, 1.67, 1.74];
export const DEFAULT_DESIGNS = ['sph', 'asph'];

export function createDefaultThicknessForm(archive) {
  const result = archive?.results ?? {};

  return {
    frameShape: 'rectangleSoft',
    A: 52,
    B: 43,
    DBL: 18,

    fittingHeightRight: Number(result.H_OD) || 22,
    fittingHeightLeft: Number(result.H_OG) || 22,
    pdRight: Number(result.DP_OD) || 32,
    pdLeft: Number(result.DP_OG) || 32,

    lensType: 'singleVision',
    addition: 1.0,
    centerThickness: 1.5,

    compareAllIndices: true,
    selectedIndices: DEFAULT_INDICES,
    selectedDesigns: DEFAULT_DESIGNS,

    rx: {
      od: { sphere: 0, cylinder: 0, axis: 0 },
      og: { sphere: 0, cylinder: 0, axis: 0 },
    },
  };
}

export function buildMaterials(selectedIndices = DEFAULT_INDICES, selectedDesigns = DEFAULT_DESIGNS) {
  const materials = [];

  for (const index of selectedIndices) {
    for (const design of selectedDesigns) {
      let coeffMinus = 1.0;
      let coeffPlus = 1.0;

      if (design === 'asph') {
        if (index === 1.5) {
          coeffMinus = 0.96;
          coeffPlus = 0.90;
        } else if (index === 1.6) {
          coeffMinus = 0.94;
          coeffPlus = 0.90;
        } else if (index === 1.67) {
          coeffMinus = 0.95;
          coeffPlus = 0.92;
        } else if (index === 1.74) {
          coeffMinus = 0.97;
          coeffPlus = 0.95;
        }
      }

      materials.push({
        label: `${index.toFixed(2)} ${design}`,
        index,
        design,
        coeffMinus,
        coeffPlus,
      });
    }
  }

  return materials;
}

export function computeThicknessStudy(form) {
  const indices = (form.compareAllIndices ? DEFAULT_INDICES : (form.selectedIndices ?? DEFAULT_INDICES))
    .map(Number)
    .filter((value) => Number.isFinite(value) && value > 1)
    .sort((a, b) => a - b);

  const designs = (form.selectedDesigns ?? DEFAULT_DESIGNS)
    .filter((value) => value === 'sph' || value === 'asph');

  const materials = buildMaterials(
    indices,
    designs.length ? designs : DEFAULT_DESIGNS
  );

  const right = computeEye({
    eye: 'od',
    sphere: Number(form.rx.od.sphere) || 0,
    cylinder: Number(form.rx.od.cylinder) || 0,
    axis: Number(form.rx.od.axis) || 0,
    fittingHeight: Number(form.fittingHeightRight) || 0,
    monocularPd: Number(form.pdRight) || 0,
    A: Number(form.A) || 0,
    B: Number(form.B) || 0,
    DBL: Number(form.DBL) || 0,
    frameShape: form.frameShape,
    centerThickness: Number(form.centerThickness) || 1.5,
    addition: Number(form.addition) || 0,
    lensType: form.lensType,
    materials,
  });

  const left = computeEye({
    eye: 'og',
    sphere: Number(form.rx.og.sphere) || 0,
    cylinder: Number(form.rx.og.cylinder) || 0,
    axis: Number(form.rx.og.axis) || 0,
    fittingHeight: Number(form.fittingHeightLeft) || 0,
    monocularPd: Number(form.pdLeft) || 0,
    A: Number(form.A) || 0,
    B: Number(form.B) || 0,
    DBL: Number(form.DBL) || 0,
    frameShape: form.frameShape,
    centerThickness: Number(form.centerThickness) || 1.5,
    addition: Number(form.addition) || 0,
    lensType: form.lensType,
    materials,
  });

  return {
    meta: {
      frameShape: form.frameShape,
      A: Number(form.A) || 0,
      B: Number(form.B) || 0,
      DBL: Number(form.DBL) || 0,
      lensType: form.lensType,
      centerThickness: Number(form.centerThickness) || 1.5,
      addition: Number(form.addition) || 0,
      materials,
    },
    right,
    left,
  };
}

function computeEye(params) {
  const {
    eye,
    sphere,
    cylinder,
    axis,
    fittingHeight,
    monocularPd,
    A,
    B,
    DBL,
    frameShape,
    centerThickness,
    addition,
    lensType,
    materials,
  } = params;

  const shapePoints = getScaledShapePoints(frameShape, A, B);
  const analysisPoints = densifyClosedShape(shapePoints, 36);
  const halfGcd = (A + DBL) / 2;
  const xShift = eye === 'od' ? -(halfGcd - monocularPd) : (halfGcd - monocularPd);
  const yShift = fittingHeight - B / 2;

  const center = { x: xShift, y: yShift };

  // diamètre utile = géométrie pure
  const furthestPoint = getFurthestPoint(analysisPoints, center);
  const usefulRadius = distance(furthestPoint, center);

  // point d'épaisseur max = géométrie + optique
  const maxThicknessPoint = getMaxThicknessPoint({
    points: analysisPoints,
    center,
    sphere,
    cylinder,
    axis,
    addition,
    lensType,
    indexForZoneSearch: materials[0]?.index ?? 1.5,
    centerThickness,
  });

  const thicknessMap = computeThicknessMap({
    points: analysisPoints,
    center,
    sphere,
    cylinder,
    axis,
    addition,
    lensType,
    index: materials[0]?.index ?? 1.5,
    centerThickness,
  });
  const maxEdgePoint = getMaxThicknessMapPoint(thicknessMap);

  const edgeThicknessValues = thicknessMap.map((p) => p.thickness);
  const maxEdgeThickness = Math.max(...edgeThicknessValues);
  const minEdgeThickness = Math.min(...edgeThicknessValues);

  const effectivePower = maxThicknessPoint.power;
  const usefulDiameter = usefulRadius * 2;

  const comparisons = materials.map((material) => {

  const indexValue = material.index;

  const profile = estimateThicknessProfile({
    power: effectivePower,
    radius: maxThicknessPoint.radius,
    index: indexValue,
    minThickness: centerThickness,
  });

  const correctedEdge = applyDesignCoefficient(
    profile.edgeThickness,
    centerThickness,
    effectivePower,
    material
  );

  const correctedCenter = applyDesignCoefficient(
    profile.centerThickness,
    centerThickness,
    effectivePower,
    material
  );

  const thicknessMapForIndex = computeThicknessMap({
    points: analysisPoints,
    center,
    sphere,
    cylinder,
    axis,
    addition,
    lensType,
    index: indexValue,
    centerThickness,
  });

  const edgeValues = thicknessMapForIndex.map((p) => p.thickness);

  const minEdgeThickness = Math.min(...edgeValues);
  const maxEdgeThickness = Math.max(...edgeValues);

  return {
    label: material.label,
    index: indexValue,
    edgeThickness: round1(correctedEdge),
    centerThickness: round1(correctedCenter),
    thicknessType: profile.thicknessType,
    minEdgeThickness: round1(minEdgeThickness),
    maxEdgeThickness: round1(maxEdgeThickness),
    cosmeticGainVs15: null,
  };
});

  const getMainThickness = (item) =>
  item?.thicknessType === 'center' ? item?.centerThickness : item?.edgeThickness;

const referenceItem =
  comparisons.find((item) => item.index === 1.5) ??
  comparisons[0];

const ref15 = getMainThickness(referenceItem) ?? centerThickness;

  const normalizedComparisons = comparisons.map((item) => ({
    ...item,
    cosmeticGainVs15: round1(ref15 - getMainThickness(item)),
  }));

  return {
    eye,
    sphere,
    cylinder,
    axis,
    effectivePower,
    fittingPoint: { x: round1(xShift), y: round1(yShift) },
    usefulRadius: round1(usefulRadius),
    usefulDiameter: round1(usefulDiameter),

    maxPoint: {
  x: round1(
    effectivePower > 0 ? maxEdgePoint.x : maxThicknessPoint.point.x
  ),
  y: round1(
    effectivePower > 0 ? maxEdgePoint.y : maxThicknessPoint.point.y
  ),
  zone: getZoneLabel(
    effectivePower > 0 ? maxEdgePoint : maxThicknessPoint.point
  ),
},

    shapePoints,
    thicknessMap,

    maxEdgeThickness: round1(maxEdgeThickness),
    minEdgeThickness: round1(minEdgeThickness),

    comparisons: normalizedComparisons,
  };
}
function estimateMeridionalThickness({ power, radius, index, centerThickness }) {
  const absPower = Math.abs(power);
  const delta = (radius * radius * absPower) / (2000 * (index - 1));
  return centerThickness + delta;
}

function estimateThicknessProfile({ power, radius, index, minThickness }) {
  const delta = (radius * radius * Math.abs(power)) / (2000 * (index - 1));

  if (power < 0) {
    return {
      edgeThickness: round1(minThickness + delta),
      centerThickness: round1(minThickness),
      thicknessType: 'edge',
    };
  }

  return {
    edgeThickness: round1(minThickness),
    centerThickness: round1(minThickness + delta),
    thicknessType: 'center',
  };
}

function getFurthestPoint(points, center) {
  let best = points[0] ?? { x: 0, y: 0 };
  let bestDist = -Infinity;

  for (const point of points) {
    const d = distance(point, center);
    if (d > bestDist) {
      best = point;
      bestDist = d;
    }
  }

  return best;
}

function getZoneLabel(point) {
  const horizontal = point.x > 6 ? 'temporal' : point.x < -6 ? 'nasal' : 'central';
  const vertical = point.y > 6 ? 'haut' : point.y < -6 ? 'bas' : 'milieu';
  return `${horizontal}-${vertical}`;
}

function distance(a, b) {
  return Math.hypot((a.x || 0) - (b.x || 0), (a.y || 0) - (b.y || 0));
}

function round1(value) {
  return Math.round(value * 10) / 10;
}

function round2(value) {
  return Math.round(value * 100) / 100;
}
function getDirectionalPowerAtPoint({ sphere, cylinder, axis, addition, lensType, center, point }) {
  const sph = Number(sphere) || 0;
  const cyl = Number(cylinder) || 0;
  const ax = normalizeAxis180(axis);
  const add = Number(addition) || 0;

  const dx = (point.x || 0) - (center.x || 0);
  const dy = (point.y || 0) - (center.y || 0);

  // angle mathématique du point
  let thetaDeg = Math.atan2(dy, dx) * 180 / Math.PI;
  if (thetaDeg < 0) thetaDeg += 360;

  // ramener la direction à un méridien optique 0..180
  if (thetaDeg >= 180) thetaDeg -= 180;

  const deltaRad = (thetaDeg - ax) * Math.PI / 180;

  let power = sph + cyl * Math.sin(deltaRad) * Math.sin(deltaRad);

  if (lensType === 'progressive' && add > 0) {
    power += add * 0.15;
  }

  return round2(power);
}
function getMaxThicknessPoint({
  points,
  center,
  sphere,
  cylinder,
  axis,
  addition,
  lensType,
  indexForZoneSearch,
  centerThickness,
}) {
  let bestPoint = points[0] ?? { x: 0, y: 0 };
  let bestRadius = 0;
  let bestPower = 0;
  let bestThickness = -Infinity;

  for (const point of points) {
    const radius = distance(point, center);

    const power = getDirectionalPowerAtPoint({
      sphere,
      cylinder,
      axis,
      addition,
      lensType,
      center,
      point,
    });

    const thickness = estimateMeridionalThickness({
      power,
      radius,
      index: indexForZoneSearch,
      centerThickness,
    });

    if (thickness > bestThickness) {
      bestPoint = point;
      bestRadius = radius;
      bestPower = power;
      bestThickness = thickness;
    }
  }

  return {
    point: bestPoint,
    radius: bestRadius,
    power: bestPower,
    thickness: round1(bestThickness),
  };
}
function computeThicknessMap({
  points,
  center,
  sphere,
  cylinder,
  axis,
  addition,
  lensType,
  index,
  centerThickness,
}) {
  const pointData = points.map((point) => {
    const radius = distance(point, center);

    const power = getDirectionalPowerAtPoint({
      sphere,
      cylinder,
      axis,
      addition,
      lensType,
      center,
      point,
    });

    const delta = (radius * radius * Math.abs(power)) / (2000 * (index - 1));

    return {
      ...point,
      radius,
      power,
      delta,
    };
  });

  const maxDelta = Math.max(...pointData.map((p) => p.delta));

  return pointData.map((p) => {
    if (p.power < 0) {
      // verre négatif : la carte représente le bord
      return {
        x: p.x,
        y: p.y,
        thickness: round1(centerThickness + p.delta),
      };
    }

    // verre positif : la carte représente le bord local réel
    return {
      x: p.x,
      y: p.y,
      thickness: round1(centerThickness + (maxDelta - p.delta)),
    };
  });
}
function getMaxThicknessMapPoint(thicknessMap) {
  if (!Array.isArray(thicknessMap) || !thicknessMap.length) {
    return { x: 0, y: 0, thickness: 0 };
  }

  return thicknessMap.reduce((best, point) =>
    point.thickness > best.thickness ? point : best,
    thicknessMap[0]
  );
}
function densifyClosedShape(points, stepsPerSegment = 24) {
  if (!Array.isArray(points) || points.length < 2) return points ?? [];

  const dense = [];

  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];

    for (let step = 0; step < stepsPerSegment; step++) {
      const t = step / stepsPerSegment;
      dense.push({
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
      });
    }
  }

  return dense;
}

function normalizeAxis180(axis) {
  let a = Number(axis) || 0;
  a = a % 180;
  if (a < 0) a += 180;
  return a;
}
function applyDesignCoefficient(thickness, minThickness, power, material) {
  const surplus = thickness - minThickness;

  const coeff = power > 0
    ? material.coeffPlus
    : material.coeffMinus;

  return minThickness + surplus * coeff;
}